import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2, Plus } from "lucide-react";

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${u}`;
};

export const PhotoUploader = ({ photos = [], onChange, max = 6, testIdPrefix = "photo" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const room = max - photos.length;
    if (room <= 0) {
      toast.error(`Max ${max} photos`);
      return;
    }
    const toUpload = list.slice(0, room);
    setUploading(true);
    const added = [];
    for (const f of toUpload) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name}: not an image`);
        continue;
      }
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name}: too large (max 5MB)`);
        continue;
      }
      try {
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post("/uploads/image", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        added.push(data.url);
      } catch (err) {
        console.error("[Upload] failed", err);
        toast.error(err.response?.data?.detail || "Upload failed");
      }
    }
    setUploading(false);
    if (added.length) {
      onChange([...photos, ...added]);
      toast.success(`${added.length} photo${added.length === 1 ? "" : "s"} added`);
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeAt = (idx) => {
    const next = photos.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div data-testid={`${testIdPrefix}-uploader`} className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        {photos.map((url, i) => (
          <div key={url + i} className="relative aspect-square rounded-xl overflow-hidden bg-[#131A26] border border-white/10 group">
            <img src={resolveUrl(url)} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              data-testid={`${testIdPrefix}-remove-${i}`}
              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 hover:bg-rose-500 flex items-center justify-center opacity-80 group-hover:opacity-100 transition-colors"
              aria-label="Remove photo"
            >
              <X size={14} className="text-white" />
            </button>
          </div>
        ))}

        {photos.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testIdPrefix}-add-btn`}
            className="aspect-square rounded-xl border-2 border-dashed border-white/15 hover:border-rose-500/50 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-rose-400 transition-colors disabled:opacity-50"
          >
            {uploading ? <Loader2 size={20} className="animate-spin" /> : <Plus size={22} />}
            <span className="text-[10px] font-mono uppercase tracking-widest">{uploading ? "Uploading" : "Add"}</span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testIdPrefix}-file-input`}
      />

      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Upload size={12} />
        <span>JPG, PNG, WebP, GIF · up to 5MB · {photos.length}/{max}</span>
      </div>
    </div>
  );
};


export const AvatarUploader = ({ url, onChange, testid = "avatar-upload" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handle = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Pick an image");
    if (file.size > 5 * 1024 * 1024) return toast.error("Too large (max 5MB)");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Avatar updated");
    } catch (err) {
      console.error("[Avatar] upload failed", err);
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-violet-500 to-rose-500 flex items-center justify-center text-xl font-display font-black">
        {url ? <img src={resolveUrl(url)} alt="" className="w-full h-full object-cover" /> : <Upload size={20} />}
      </div>
      <div className="flex-1">
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          data-testid={`${testid}-btn`}
          className="h-10 px-4 rounded-lg bg-white/10 hover:bg-white/15 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Uploading..." : "Change photo"}
        </button>
        {url && (
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testid}-remove`}
            className="ml-2 h-10 px-3 rounded-lg text-xs text-slate-400 hover:text-rose-400"
          >
            Remove
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handle(e.target.files?.[0])}
      />
    </div>
  );
};
